/** Small, pure edits of a mapping recipe that several parts of the review make. */

import type {
  EntityDescriptor,
  MissingLink,
  Proposal,
  SubjectEntity,
} from '@/services/api/structuredIngest'
import { canEnableMappingMerge } from './identity'

/** Edited constant values must be checked against the selected property's meaning again. */
export function withSubjectValue(item: SubjectEntity, value: string): SubjectEntity {
  return { ...item, identity_value: value, identity_error: null, identity_properties: undefined }
}

/** Disabling merging preserves the key configuration and all ordinary property mappings. */
export function withMappingMerge<T extends SubjectEntity | EntityDescriptor>(
  item: T,
  enabled: boolean
): T {
  if (enabled && !canEnableMappingMerge(item)) return item
  return {
    ...item,
    merge_enabled: enabled,
    identity_error: null,
    identity_properties: enabled ? undefined : item.identity_properties,
  }
}

/** The list with the item of that name replaced by what `edit` makes of it. */
export function editByName<T extends { name: string }>(
  items: T[] | undefined,
  name: string,
  edit: (item: T) => T
): T[] | undefined {
  return items?.map(item => (item.name === name ? edit(item) : item))
}

/** Turns one mapping (subject, entity or relationship, by name) on or off. */
export function withEnabled(recipe: Proposal, name: string, enabled: boolean): Proposal {
  return {
    ...recipe,
    subject_entities: editByName(recipe.subject_entities, name, item => ({ ...item, enabled })),
    entity_descriptors: editByName(recipe.entity_descriptors, name, item => ({ ...item, enabled })),
    relationships: editByName(recipe.relationships, name, item => ({ ...item, enabled })),
    columns: recipe.columns?.map(column =>
      !enabled && column.entity === name ? { ...column, extract_entities: false } : column
    ),
  }
}

/** Keep each source row separate while preserving its fields and former merge configuration. */
export function withSourceRowIdentity(recipe: Proposal, name: string, note: string): Proposal {
  return {
    ...recipe,
    entity_descriptors: editByName(recipe.entity_descriptors, name, item => ({
      ...withMappingMerge(item, false),
      note,
    })),
  }
}

/** A phone number written several ways is one entity, or each spelling stays its own. */
export function withPhoneFormat<T extends SubjectEntity | EntityDescriptor>(
  item: T,
  international: boolean
): T {
  return { ...item, identity_rule: international ? 'e164' : 'exact' }
}

/** Adds the relationship the server suggested for a link the mapping leaves out. */
export function withLink(recipe: Proposal, link: MissingLink): Proposal {
  return { ...recipe, relationships: [...(recipe.relationships ?? []), link.relationship] }
}

export function withWindows(recipe: Proposal, enabled: boolean): Proposal {
  return { ...recipe, windows: { ...recipe.windows, enabled } }
}
